import styled from '@emotion/styled';
import { CncReport } from '@gameonly/core';
import React, { MouseEvent, MouseEventHandler } from 'react';

import ButtonWithConfirm from '../ui/ButtonWithConfirm';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';

import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import { useTranslation } from 'react-i18next';
import BaseBlock from '../ui/Block';

interface ReportCardProps {
  report: CncReport;
  onClick?: MouseEventHandler<HTMLButtonElement | HTMLDivElement>;
  onDelete?: (event: any) => Promise<Partial<CncReport>>;
}

const Block = styled(BaseBlock)`
  position: relative;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: unset;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
  height: 150px;
  min-width: 200px;
  border: 1px solid black;
  .hzr {
    font-size: 10px;
  }
  svg {
    transition: fill 0.2s ease-in-out;
  }
  > div:last-of-type {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
    > div {
      flex: 1;
    }
  }
  &:hover {
    border: 1px solid black;
    background-color: var(--grey);
    .h6r {
      font-weight: bold;
    }
  }
`;

const Header = styled.div`
  width: 100%;
  display: flex;
  gap: 16px;
`;

const TitleContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const Footer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  gap: 16px;
`;

const CncReportCard = ({ report, onClick, onDelete }: ReportCardProps) => {
  const { t } = useTranslation(['cnc-reports', 'model']);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(null);
  };

  const handleDelete = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onDelete && (await onDelete(event));
  };

  const isValidated = !!report.validatedAt;

  return (
    <Block onClick={onClick}>
      <Header>
        {isValidated && (
          <Chip
            icon={<CheckCircleOutlineIcon />}
            label={t('cnc-reports:card.validated')}
            color="success"
            variant="outlined"
          />
        )}
      </Header>
      {!isValidated && (
        <>
          <IconButton
            sx={{ position: 'absolute', top: '6px', right: '6px', padding: 0 }}
            onClick={handleOpenMenu}
          >
            <MoreHorizOutlinedIcon />
          </IconButton>
          <Menu anchorEl={anchorEl} open={openMenu} onClose={handleCloseMenu}>
            <MenuItem disabled={!!report.validatedAt}>
              <ButtonWithConfirm
                onConfirm={handleDelete}
                modalTitle={t('cnc-reports:card.delete.title')}
                modalContent={t('cnc-reports:card.delete.content')}
              >
                {t('cnc-reports:card.delete.button')}
              </ButtonWithConfirm>
            </MenuItem>
          </Menu>
        </>
      )}
      <TitleContainer>
        <p className="h5b">{report.name}</p>
      </TitleContainer>
      <Footer>
        <p className="hzr">
          {t('cnc-reports:card.createdAt', {
            date: new Date(report.createdAt).toLocaleDateString(),
          })}
        </p>
      </Footer>
    </Block>
  );
};

export default CncReportCard;
